import { useEffect, useState } from "react";
import {
  deleteTriggerRequest,
  getSmartTriggerSettings,
  saveNumberTemplate,
  saveSmartTriggerEnabled,
  saveTriggerRequest,
  TriggerChannel,
  TriggerButton,
  TriggerInput,
  TriggerRequest,
} from "../../../api/leads.service";
import { getInstagramMediaLibrary, InstagramLibraryMedia, uploadInstagramMedia } from "../../../api/instagram.service";
import { getTemplate, getTemplatesList } from "../../../api/login.service";
import { businessId } from "../inbox/businessSetup";

const SWITCH_KEY = "fd24.smartTriggerHourly.v1";

const TABS: { id: TriggerChannel; label: string }[] = [
  { id: "platform", label: "Platform" },
  { id: "instagram", label: "Instagram" },
  { id: "whatsapp", label: "WhatsApp" },
  { id: "web", label: "Web" },
];

function loadHourlyOn(): boolean {
  try {
    const all = JSON.parse(localStorage.getItem(SWITCH_KEY) || "{}");
    return all[businessId()] !== false;
  } catch {
    return true;
  }
}

function saveHourlyOn(enabled: boolean) {
  const all = JSON.parse(localStorage.getItem(SWITCH_KEY) || "{}");
  all[businessId()] = enabled;
  localStorage.setItem(SWITCH_KEY, JSON.stringify(all));
}

function ImageLibrary({
  selectedUrl,
  onSelect,
  onClose,
}: {
  selectedUrl: string;
  onSelect: (url: string) => void;
  onClose: () => void;
}) {
  const [media, setMedia] = useState<InstagramLibraryMedia[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    let active = true;
    getInstagramMediaLibrary()
      .then((list) => active && setMedia(list.filter((item) => item.kind === "IMAGE")))
      .catch(() => active && setLoadError("Could not load your uploads."))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, []);

  return (
    <div className="mt-2 rounded-lg border border-gray-200 p-3">
      <div className="mb-2 flex items-center justify-between">
        <p className="text-sm font-medium text-gray-700">Your uploads</p>
        <button type="button" onClick={onClose} className="text-xs text-gray-500">
          Close
        </button>
      </div>
      {loading ? <p className="text-xs text-gray-500">Loading...</p> : null}
      {loadError ? <p className="text-xs text-red-600">{loadError}</p> : null}
      {!loading && !loadError && media.length === 0 ? (
        <p className="text-xs text-gray-500">Nothing uploaded yet. Use Upload to add an image.</p>
      ) : null}
      <div className="grid max-h-64 grid-cols-3 gap-2 overflow-y-auto sm:grid-cols-4">
        {media.map((item) => (
          <button
            key={item.key}
            type="button"
            disabled={!item.usable}
            onClick={() => onSelect(item.url)}
            className={`relative aspect-square overflow-hidden rounded-md border bg-gray-50 ${
              item.url === selectedUrl ? "border-blue-600 ring-2 ring-blue-500" : "border-gray-100"
            } ${item.usable ? "hover:ring-2 hover:ring-blue-500" : "cursor-not-allowed opacity-40"}`}
          >
            <img src={item.url} alt="" loading="lazy" className="h-full w-full object-cover" />
          </button>
        ))}
      </div>
    </div>
  );
}

type ListedTemplate = { id: string; name: string; language: string };

function languageOf(raw: unknown): string {
  if (typeof raw === "string") return raw.trim();
  const code = (raw as { code?: string } | null)?.code;
  return typeof code === "string" ? code.trim() : "";
}

function fieldAbout(body: string, key: string): string {
  const token = `{{${key}}}`;
  const at = body.indexOf(token);
  if (at < 0) return `Value for ${token}`;
  const around = body.slice(Math.max(0, at - 24), at + token.length + 24).replace(/\s+/g, " ").trim();
  return `${token} is the blank in: ${around}`;
}

function previewText(body: string, fields: TriggerInput[]): string {
  return body.replace(/\{\{\s*([^}]+)\s*\}\}/g, (token, key) => {
    const number = Number(String(key).trim());
    const filled = Number.isFinite(number) && number > 0 ? fields[number - 1]?.description.trim() : "";
    return filled || token;
  });
}

function templateParts(full: { components?: Array<{ type?: string; text?: string; format?: string; buttons?: Array<{ text?: string }> }>; language?: unknown }): {
  body: string;
  buttons: string[];
  needsImage: boolean;
  language: string;
  keys: string[];
} {
  const components = Array.isArray(full?.components) ? full.components : [];
  const bodyPart = components.find((part) => String(part.type || "").toUpperCase() === "BODY");
  const header = components.find((part) => String(part.type || "").toUpperCase() === "HEADER");
  const buttonPart = components.find((part) => String(part.type || "").toUpperCase() === "BUTTONS");
  const body = String(bodyPart?.text || "");
  return {
    body,
    buttons: (buttonPart?.buttons || []).map((button) => String(button.text || "").trim()).filter(Boolean),
    needsImage: String(header?.format || "").toUpperCase() === "IMAGE",
    language: languageOf(full?.language),
    keys: variableKeys(body),
  };
}

function variableKeys(content: string): string[] {
  const found = content.match(/\{\{([^}]+)\}\}/g) || [];
  const keys: string[] = [];
  found.forEach((token) => {
    const key = token.replace(/\{\{|\}\}/g, "").trim();
    if (key && !keys.includes(key)) keys.push(key);
  });
  return keys;
}

function actionLabel(request: TriggerRequest): string {
  if (request.channel === "whatsapp" && request.templateName) return `Send template ${request.templateName}`;
  if ((request.channel || "platform") === "platform") return "Waiting for us to create and approve";
  const parts = [
    request.sends?.includes("phone") ? "phone number" : "",
    request.sends?.includes("text") ? "text" : "",
    request.sends?.includes("image") ? "image" : "",
  ].filter(Boolean);
  return parts.length ? `Send ${parts.join(" and ")}` : "Message";
}

export default function SmartTriggersPage() {
  const [tab, setTab] = useState<TriggerChannel>("platform");
  const [query, setQuery] = useState("");
  const [hourlyOn, setHourlyOn] = useState(loadHourlyOn);
  const [requests, setRequests] = useState<TriggerRequest[]>([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [detail, setDetail] = useState("");
  const [sendPhone, setSendPhone] = useState(true);
  const [sendText, setSendText] = useState(true);
  const [inputs, setInputs] = useState<TriggerInput[]>([]);
  const [messageText, setMessageText] = useState("");
  const [buttons, setButtons] = useState<TriggerButton[]>([]);
  const [imageUrl, setImageUrl] = useState("");
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [templateName, setTemplateName] = useState("");
  const [languageCode, setLanguageCode] = useState("");
  const [needsImage, setNeedsImage] = useState(false);
  const [templates, setTemplates] = useState<ListedTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templateBody, setTemplateBody] = useState("");
  const [templateButtons, setTemplateButtons] = useState<string[]>([]);
  const [numberTemplateOn, setNumberTemplateOn] = useState(false);
  const [numberTemplateName, setNumberTemplateName] = useState("");
  const [numberTemplateLanguage, setNumberTemplateLanguage] = useState("en_US");
  const [numberTemplateNote, setNumberTemplateNote] = useState("");
  const [numberTemplateImage, setNumberTemplateImage] = useState("");
  const [numberTemplateNeedsImage, setNumberTemplateNeedsImage] = useState(false);
  const [numberTemplateLibraryOpen, setNumberTemplateLibraryOpen] = useState(false);
  const [numberTemplateFields, setNumberTemplateFields] = useState<string[]>([]);
  const [numberTemplateValues, setNumberTemplateValues] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    getSmartTriggerSettings(businessId())
      .then((settings) => {
        if (cancelled || !settings) return;
        saveHourlyOn(settings.enabled);
        setHourlyOn(settings.enabled);
        setRequests(settings.requests);
        setNumberTemplateOn(settings.numberTemplate.enabled);
        setNumberTemplateName(settings.numberTemplate.templateName);
        setNumberTemplateLanguage(settings.numberTemplate.languageCode || "en_US");
        setNumberTemplateImage(settings.numberTemplate.imageUrl || "");
        setNumberTemplateValues(settings.numberTemplate.values || []);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (tab !== "instagram" && !(open && tab === "whatsapp")) return;
    const wabaId = sessionStorage.getItem("wabaId") || "";
    if (!wabaId) return;
    let cancelled = false;
    setTemplatesLoading(true);
    getTemplatesList(wabaId, 100)
      .then((data) => {
        if (cancelled) return;
        const rows = Array.isArray(data?.data) ? data.data : [];
        setTemplates(
          rows
            .filter((item: { status?: string; id?: string; name?: string }) => item.status === "APPROVED" && item.name)
            .map((item: { id?: string; name?: string; language?: unknown }) => ({
              id: String(item.id || item.name),
              name: String(item.name),
              language: languageOf(item.language),
            }))
        );
      })
      .catch(() => {
        if (!cancelled) setTemplates([]);
      })
      .finally(() => {
        if (!cancelled) setTemplatesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, tab]);

  useEffect(() => {
    if (tab !== "instagram" || !numberTemplateOn || !numberTemplateName) return;
    const listed = templates.find((item) => item.name === numberTemplateName);
    if (!listed) return;
    let cancelled = false;
    getTemplate(listed.id)
      .then((full) => {
        if (cancelled) return;
        const parts = templateParts(full);
        setNumberTemplateNeedsImage(parts.needsImage);
        setNumberTemplateFields(parts.keys.map((key) => fieldAbout(parts.body, key)));
        if (parts.language) setNumberTemplateLanguage(parts.language);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [tab, numberTemplateOn, numberTemplateName, templates]);

  useEffect(() => {
    if (!open || tab !== "whatsapp" || !templateName || templateBody) return;
    const listed = templates.find((item) => item.name === templateName);
    if (!listed) return;
    let cancelled = false;
    getTemplate(listed.id)
      .then((full) => {
        if (cancelled) return;
        const parts = templateParts(full);
        setTemplateBody(parts.body);
        setTemplateButtons(parts.buttons);
        setNeedsImage(parts.needsImage);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [open, tab, templateName, templateBody, templates]);

  const toggleHourly = async () => {
    const next = !hourlyOn;
    setHourlyOn(next);
    saveHourlyOn(next);
    setError("");
    try {
      await saveSmartTriggerEnabled(businessId(), next);
    } catch {
      setError("Could not save the switch. The hourly job keeps running until this is saved.");
    }
  };

  const openForm = (request?: TriggerRequest) => {
    setError("");
    setEditingId(request?.id || null);
    setName(request?.name || "");
    setDetail(request?.text || "");
    setSendPhone(request ? Boolean(request.sends?.includes("phone")) : true);
    setSendText(request ? Boolean(request.sends?.includes("text")) : true);
    setMessageText(request?.messageText || "");
    setButtons((request?.buttons || []).map((item) => ({ ...item })));
    setImageUrl(request?.imageUrl || "");
    setInputs(request?.inputs?.length ? request.inputs.map((item) => ({ ...item })) : []);
    setTemplateName(request?.templateName || "");
    setLanguageCode(request?.languageCode || "");
    setNeedsImage(Boolean(request?.imageUrl));
    setOpen(true);
  };

  const closeForm = () => {
    setOpen(false);
    setEditingId(null);
    setName("");
    setDetail("");
    setMessageText("");
    setButtons([]);
    setImageUrl("");
    setInputs([]);
    setTemplateName("");
    setLanguageCode("");
    setNeedsImage(false);
    setTemplateBody("");
    setTemplateButtons([]);
    setLibraryOpen(false);
  };

  const chooseTemplate = async (id: string) => {
    const listed = templates.find((item) => item.id === id);
    if (!listed) return;
    setError("");
    setTemplatesLoading(true);
    try {
      const full = await getTemplate(id);
      const parts = templateParts(full);
      const language = parts.language || listed.language;
      if (!language) {
        setError("This template has no language.");
        return;
      }
      setTemplateName(listed.name);
      setLanguageCode(language);
      setNeedsImage(parts.needsImage);
      setTemplateBody(parts.body);
      setTemplateButtons(parts.buttons);
      setInputs(parts.keys.map((_, index) => ({
        name: `field${index + 1}`,
        description: "",
      })));
      if (!parts.needsImage) setImageUrl("");
    } catch {
      setError("Could not load that template.");
    } finally {
      setTemplatesLoading(false);
    }
  };

  const uploadImage = async (file?: File) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Choose an image.");
      return;
    }
    setUploading(true);
    setError("");
    try {
      const uploaded = await uploadInstagramMedia(file);
      if (!uploaded.success || !uploaded.url) {
        setError(uploaded.error || "Could not upload the image.");
        return;
      }
      setImageUrl(uploaded.url);
    } catch {
      setError("Could not upload the image.");
    } finally {
      setUploading(false);
    }
  };

  const sendRequest = async () => {
    const trimmedName = name.trim();
    const trimmedDetail = detail.trim();
    const trimmedMessage = messageText.trim();
    const sends = (tab === "instagram"
      ? [sendPhone ? "phone" as const : null, trimmedMessage ? "text" as const : null, imageUrl ? "image" as const : null]
      : [sendPhone ? "phone" as const : null, sendText ? "text" as const : null]
    ).filter((item): item is "phone" | "text" | "image" => Boolean(item));
    const cleanedInputs = inputs
      .map((item) => ({ name: item.name.trim(), description: item.description.trim() }))
      .filter((item) => item.name || item.description);
    if ((tab === "instagram" || tab === "whatsapp") && cleanedInputs.some((item) => !item.name || !item.description)) {
      setError("Name each input and say what the model should put in it.");
      return;
    }
    if (tab === "whatsapp" && (!templateName.trim() || !languageCode.trim())) {
      setError("Choose an approved template.");
      return;
    }
    if (tab === "whatsapp" && needsImage && !imageUrl) {
      setError("This template needs an image.");
      return;
    }
    const cleanedButtons = tab === "instagram"
      ? buttons
        .map((item) => ({
          title: item.title.trim().slice(0, 20),
          triggerId: "",
          kind: item.kind === "url" ? "url" as const : "quick" as const,
          url: item.kind === "url" ? (item.url || "").trim() : "",
        }))
        .filter((item) => item.title)
        .slice(0, 3)
      : [];
    if (cleanedButtons.some((item) => item.kind === "url" && !/^https:\/\//i.test(item.url))) {
      setError("A link button needs a URL that starts with https://");
      return;
    }
    if (cleanedButtons.length && !trimmedMessage) {
      setError("Button messages need message text.");
      return;
    }
    if (!trimmedName || !trimmedDetail) return;
    if (tab !== "platform" && tab !== "whatsapp" && sends.length === 0) {
      setError(tab === "instagram" ? "Add a phone number, message text, or an image." : "Choose a phone number, text, or both.");
      return;
    }
    setSending(true);
    setError("");
    try {
      const saved = await saveTriggerRequest(businessId(), {
        id: editingId || undefined,
        name: trimmedName,
        text: trimmedDetail,
        messageText: tab === "instagram" ? trimmedMessage : tab === "whatsapp" ? (templateBody.trim() || trimmedMessage) : undefined,
        imageUrl: tab === "instagram" || tab === "whatsapp" ? imageUrl : undefined,
        inputs: tab === "instagram" || tab === "whatsapp" ? cleanedInputs : undefined,
        templateName: tab === "whatsapp" ? templateName.trim() : undefined,
        languageCode: tab === "whatsapp" ? languageCode.trim() : undefined,
        route: (() => {
          if (tab !== "instagram" || !editingId) return undefined;
          const stored = requests.find((item) => item.id === editingId)?.route;
          return stored === "first_message" || stored === "match" ? stored : undefined;
        })(),
        buttons: tab === "instagram" ? cleanedButtons : undefined,
        channel: tab,
        sends: tab === "platform" || tab === "whatsapp" ? (imageUrl && tab === "whatsapp" ? ["image"] : undefined) : sends,
      });
      setRequests((current) => (
        editingId ? current.map((item) => (item.id === editingId ? saved : item)) : current.concat(saved)
      ));
      closeForm();
    } catch {
      setError(editingId ? "Could not save the trigger. Try again." : "Could not send the request. Try again.");
    } finally {
      setSending(false);
    }
  };

  const removeRequest = async (id: string) => {
    setSending(true);
    setError("");
    try {
      await deleteTriggerRequest(businessId(), id);
      setRequests((current) => current.filter((item) => item.id !== id));
      setConfirmDeleteId(null);
    } catch {
      setError("Could not delete the trigger. Try again.");
    } finally {
      setSending(false);
    }
  };

  const templateReady = tab === "whatsapp"
    && Boolean(templateName)
    && inputs.every((item) => item.description.trim())
    && (!needsImage || Boolean(imageUrl));
  const needle = query.trim().toLowerCase();
  const hourlyVisible = tab === "platform" && ("every hour add or remove tags and move stages based on messages".includes(needle) || needle === "");
  const visibleRequests = requests.filter((request) => {
    const channel = request.channel || "platform";
    if (channel !== tab) return false;
    const haystack = `${request.name} ${request.text}`.toLowerCase();
    return haystack.includes(needle);
  });
  const tabLabel = TABS.find((item) => item.id === tab)?.label || "Platform";

  return (
    <div className="mx-auto max-w-6xl py-4">
      <div className="mb-4 flex items-center gap-3">
        <h2 className="text-2xl font-semibold text-gray-900">Smart Triggers</h2>
        <button
          type="button"
          onClick={() => openForm()}
          className="ml-auto rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white"
        >
          {tab === "platform" ? "Request a trigger" : "Add a trigger"}
        </button>
      </div>

      <div className="mb-4 flex gap-1 border-b border-gray-200">
        {TABS.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => {
              setTab(item.id);
              setConfirmDeleteId(null);
              closeForm();
            }}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium ${
              tab === item.id ? "border-blue-600 text-blue-700" : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            {item.label}
          </button>
        ))}
      </div>

      {tab === "instagram" ? (
        <div className="mb-4 rounded-xl border border-gray-200 p-4">
          <label className="flex items-center gap-2 text-sm font-medium text-gray-800">
            <input type="checkbox" checked={numberTemplateOn} onChange={(event) => setNumberTemplateOn(event.target.checked)} />
            When a WhatsApp number arrives, send this template
          </label>
          {numberTemplateOn ? (
            <>
            <select
              value={templates.find((item) => item.name === numberTemplateName)?.id || numberTemplateName}
              onChange={async (event) => {
                const listed = templates.find((item) => item.id === event.target.value);
                setNumberTemplateName(listed?.name || "");
                setNumberTemplateLanguage(listed?.language || "");
                setNumberTemplateFields([]);
                setNumberTemplateNeedsImage(false);
                if (!listed) return;
                try {
                  const parts = templateParts(await getTemplate(listed.id));
                  setNumberTemplateNeedsImage(parts.needsImage);
                  setNumberTemplateFields(parts.keys.map((key) => fieldAbout(parts.body, key)));
                  setNumberTemplateValues(parts.keys.map(() => ""));
                  if (parts.language) setNumberTemplateLanguage(parts.language);
                  if (!parts.needsImage) setNumberTemplateImage("");
                } catch {
                  setNumberTemplateNote("Could not read that template.");
                }
              }}
              className="mt-3 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
            >
              <option value="">{templatesLoading ? "Loading templates..." : numberTemplateName || "Choose a template"}</option>
              {numberTemplateName && !templates.some((item) => item.name === numberTemplateName) ? (
                <option value={numberTemplateName}>{numberTemplateName}</option>
              ) : null}
              {templates.map((item) => (
                <option key={item.id} value={item.id}>{item.name}</option>
              ))}
            </select>
            {numberTemplateNeedsImage ? (
              <div className="mt-3">
                <p className="text-sm text-gray-700">This template header is an image. Choose the picture to send.</p>
                <div className="mt-2 flex gap-2">
                  <label className="inline-flex cursor-pointer items-center rounded-lg border border-gray-200 px-3 py-2 text-sm text-blue-700">
                    Upload
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={async (event) => {
                        const file = event.target.files?.[0];
                        event.target.value = "";
                        if (!file) return;
                        const uploaded = await uploadInstagramMedia(file).catch(() => null);
                        if (uploaded?.url) setNumberTemplateImage(uploaded.url);
                      }}
                    />
                  </label>
                  <button
                    type="button"
                    onClick={() => setNumberTemplateLibraryOpen((openNow) => !openNow)}
                    className="rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700"
                  >
                    Library
                  </button>
                </div>
                {numberTemplateLibraryOpen ? (
                  <ImageLibrary
                    selectedUrl={numberTemplateImage}
                    onClose={() => setNumberTemplateLibraryOpen(false)}
                    onSelect={(url) => {
                      setNumberTemplateImage(url);
                      setNumberTemplateLibraryOpen(false);
                    }}
                  />
                ) : null}
                {numberTemplateImage ? <img src={numberTemplateImage} alt="" className="mt-2 h-16 w-16 rounded object-cover" /> : null}
              </div>
            ) : null}
            {numberTemplateFields.map((about) => (
              <p key={about} className="mt-3 text-sm text-gray-700">{about} This is filled with the name they sent. If there is no name, it is hello.</p>
            ))}
            </>
          ) : null}
          {numberTemplateOn ? (
            <>
              <button
                type="button"
                onClick={async () => {
                  setNumberTemplateNote("");
                  setError("");
                  try {
                    if (numberTemplateNeedsImage && !numberTemplateImage.startsWith("https://")) {
                      setNumberTemplateNote("This template needs an image.");
                      return;
                    }
                    const saved = await saveNumberTemplate(businessId(), {
                      enabled: numberTemplateOn,
                      templateName: numberTemplateName.trim(),
                      languageCode: numberTemplateLanguage.trim(),
                      imageUrl: numberTemplateImage,
                      values: numberTemplateValues.map((item) => item.trim()).filter(Boolean),
                      bodyCount: numberTemplateFields.length,
                    });
                    setNumberTemplateOn(saved.enabled);
                    setNumberTemplateName(saved.templateName);
                    setNumberTemplateLanguage(saved.languageCode);
                    setNumberTemplateImage(saved.imageUrl || "");
                    setNumberTemplateValues(saved.values || []);
                    setNumberTemplateNote(`Saved. ${saved.templateName} is sent when a WhatsApp number arrives.`);
                  } catch {
                    setNumberTemplateNote("Could not save. Choose a template first.");
                  }
                }}
                className="mt-3 cursor-pointer rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white"
              >
                Save template
              </button>
              {numberTemplateNote ? (
                <p className="mt-2 text-sm text-gray-700">{numberTemplateNote}</p>
              ) : null}
            </>
          ) : null}
        </div>
      ) : null}

      <input
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="Search for any rule"
        className="mb-4 w-full rounded-lg border border-gray-200 px-4 py-2.5 text-sm"
      />

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
        <div className="grid grid-cols-[1.1fr_1.6fr_1.3fr_auto] bg-gray-50 px-4 py-3 text-sm text-gray-500">
          <span>Rule Name</span>
          <span>Trigger</span>
          <span>Action</span>
          <span />
        </div>
        {hourlyVisible ? (
          <div className="grid grid-cols-[1.1fr_1.6fr_1.3fr_auto] items-center gap-3 border-t border-gray-100 px-4 py-4 text-sm">
            <span className="truncate font-medium text-gray-800">Every hour</span>
            <span className="truncate text-gray-600">Every 1 hour, for each lead</span>
            <span className="truncate text-gray-600">Add or remove tags and move stages based on messages</span>
            <button
              type="button"
              role="switch"
              aria-checked={hourlyOn}
              aria-label="Hourly smart trigger"
              onClick={toggleHourly}
              className={`relative h-6 w-11 shrink-0 rounded-full ${hourlyOn ? "bg-blue-600" : "bg-gray-300"}`}
            >
              <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white ${hourlyOn ? "left-5" : "left-0.5"}`} />
            </button>
          </div>
        ) : null}
        {tab === "instagram" && numberTemplateOn && numberTemplateName ? (
          <div className="grid grid-cols-[1.1fr_1.6fr_1.3fr_auto] items-center gap-3 border-t border-gray-100 px-4 py-4 text-sm">
            <span className="truncate font-medium text-gray-800">{numberTemplateName}</span>
            <span className="truncate text-gray-600">When a WhatsApp number arrives</span>
            <span className="flex min-w-0 items-center gap-2 overflow-hidden text-gray-600">
              {numberTemplateImage ? (
                <span className="inline-flex h-8 w-8 shrink-0 overflow-hidden rounded">
                  <img src={numberTemplateImage} alt="" className="h-full w-full object-cover" />
                </span>
              ) : null}
              <span className="truncate leading-8">Send template {numberTemplateName}</span>
            </span>
            <span />
          </div>
        ) : null}
        {visibleRequests.map((request) => (
          <div key={request.id} className="grid grid-cols-[1.1fr_1.6fr_1.3fr_auto] items-center gap-3 border-t border-gray-100 px-4 py-4 text-sm">
            <span className="truncate font-medium text-gray-800" title={request.name}>{request.name}</span>
            <span className="truncate text-gray-600" title={request.text}>{request.text}</span>
            <span className="flex min-w-0 items-center gap-2 overflow-hidden text-gray-600" title={actionLabel(request)}>
              {request.imageUrl ? (
                <span className="inline-flex h-8 w-8 shrink-0 overflow-hidden rounded">
                  <img src={request.imageUrl} alt="" className="h-full w-full object-cover" />
                </span>
              ) : null}
              <span className="truncate leading-8">{actionLabel(request)}</span>
            </span>
            <span className="flex items-center gap-2">
              <button type="button" onClick={() => openForm(request)} className="text-sm text-blue-700">
                Edit
              </button>
              {confirmDeleteId === request.id ? (
                <button
                  type="button"
                  onClick={() => removeRequest(request.id)}
                  disabled={sending}
                  className="text-sm text-red-600 disabled:text-gray-300"
                >
                  Confirm
                </button>
              ) : (
                <button type="button" onClick={() => setConfirmDeleteId(request.id)} className="text-sm text-gray-500">
                  Delete
                </button>
              )}
            </span>
          </div>
        ))}
        {!hourlyVisible && visibleRequests.length === 0 ? (
          <p className="px-4 py-8 text-sm text-gray-500">
            {needle ? "No rules match this search." : `No triggers on ${tabLabel} yet.`}
          </p>
        ) : null}
      </div>
      {error ? <p className="mt-2 text-xs text-red-600">{error}</p> : null}

      {open ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl bg-white p-5 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900">
              {editingId ? "Edit trigger" : tab === "platform" ? "Request a trigger" : "Add a trigger"}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {tab === "platform"
                ? "The hourly trigger is the same for every business. Describe what you need. We create it and approve it."
                : tab === "instagram"
                  ? "Jev reads the saved when-to-call sentence and picks one tool. Inputs are optional. The message, image, and phone number are what the tool sends."
                  : tab === "whatsapp"
                  ? "Jev reads the when-to-call sentence. The approved template name and language are saved on this trigger and used when it is sent."
                  : "Describe when this should run. The message can include a phone number, text, or both."}
            </p>
            <label className="mt-4 block text-sm text-gray-600">
              {tab === "instagram" ? "Tool name" : "Trigger name"}
              <input
                value={name}
                onChange={(event) => setName(event.target.value)}
                className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
              />
            </label>
            <label className="mt-3 block text-sm text-gray-600">
              {tab === "instagram" || tab === "whatsapp" ? "When should the model call this tool?" : "What should this trigger do?"}
              <textarea
                value={detail}
                onChange={(event) => setDetail(event.target.value)}
                rows={4}
                className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
              />
            </label>
            {tab === "instagram" ? (
              <div className="mt-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-600">Inputs the model can fill</p>
                  <button
                    type="button"
                    onClick={() => setInputs((current) => current.concat({ name: "", description: "" }))}
                    className="text-sm text-blue-700"
                  >
                    Add input
                  </button>
                </div>
                {inputs.length === 0 ? (
                  <p className="mt-1 text-xs text-gray-500">None. Add one only if the model should pass a value, such as a day or a name.</p>
                ) : null}
                {inputs.map((input, index) => (
                  <div key={index} className="mt-2 rounded-lg border border-gray-200 p-3">
                    <label className="block text-sm text-gray-600">
                      Input name
                      <input
                        value={input.name}
                        onChange={(event) => setInputs((current) => current.map((item, itemIndex) => (
                          itemIndex === index ? { ...item, name: event.target.value } : item
                        )))}
                        className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                      />
                    </label>
                    <label className="mt-2 block text-sm text-gray-600">
                      What the model should put here
                      <input
                        value={input.description}
                        onChange={(event) => setInputs((current) => current.map((item, itemIndex) => (
                          itemIndex === index ? { ...item, description: event.target.value } : item
                        )))}
                        className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                      />
                    </label>
                    <button
                      type="button"
                      onClick={() => setInputs((current) => current.filter((_, itemIndex) => itemIndex !== index))}
                      className="mt-2 text-sm text-gray-500"
                    >
                      Remove input
                    </button>
                  </div>
                ))}
                <p className="mt-4 text-sm font-medium text-gray-800">What the tool sends</p>
                <label className="mt-2 block text-sm text-gray-600">
                  Message text
                  <textarea
                    value={messageText}
                    onChange={(event) => setMessageText(event.target.value)}
                    rows={3}
                    className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  />
                </label>
                <div className="mt-3">
                  <p className="text-sm text-gray-600">Image</p>
                  <div className="mt-1 flex gap-2">
                    <label
                      className={`flex cursor-pointer items-center rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-blue-600 ${
                        uploading ? "pointer-events-none opacity-50" : ""
                      }`}
                    >
                      {uploading ? "Uploading..." : "Upload"}
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(event) => {
                          const file = event.target.files?.[0];
                          event.target.value = "";
                          uploadImage(file);
                        }}
                      />
                    </label>
                    <button
                      type="button"
                      onClick={() => setLibraryOpen((open) => !open)}
                      className="rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700"
                    >
                      Library
                    </button>
                  </div>
                  {libraryOpen ? (
                    <ImageLibrary
                      selectedUrl={imageUrl}
                      onClose={() => setLibraryOpen(false)}
                      onSelect={(url) => {
                        setImageUrl(url);
                        setLibraryOpen(false);
                      }}
                    />
                  ) : null}
                </div>
                {imageUrl ? (
                  <div className="mt-2 flex items-center gap-3">
                    <img src={imageUrl} alt="" className="h-16 w-16 rounded object-cover" />
                    <button type="button" onClick={() => setImageUrl("")} className="text-sm text-gray-500">
                      Remove image
                    </button>
                  </div>
                ) : null}
                <div className="mt-4">
                    <div className="mt-3">
                        <p className="text-sm font-medium text-gray-800">Buttons</p>
                        <p className="mt-1 text-xs text-gray-500">Up to 3. Quick reply shows on the customer’s phone. A link button opens a URL.</p>
                        {buttons.map((button, index) => (
                          <div key={index} className="mt-2 rounded-lg border border-gray-200 p-3">
                            <div className="flex gap-4 text-sm text-gray-800">
                              <label className="flex items-center gap-2">
                                <input
                                  type="radio"
                                  name={`button-kind-${index}`}
                                  checked={button.kind !== "url"}
                                  onChange={() => setButtons((current) => current.map((item, itemIndex) => (
                                    itemIndex === index ? { ...item, kind: "quick" } : item
                                  )))}
                                />
                                Quick reply
                              </label>
                              <label className="flex items-center gap-2">
                                <input
                                  type="radio"
                                  name={`button-kind-${index}`}
                                  checked={button.kind === "url"}
                                  onChange={() => setButtons((current) => current.map((item, itemIndex) => (
                                    itemIndex === index ? { ...item, kind: "url" } : item
                                  )))}
                                />
                                Link
                              </label>
                            </div>
                            <input
                              value={button.title}
                              maxLength={20}
                              placeholder="Button title"
                              onChange={(event) => setButtons((current) => current.map((item, itemIndex) => (
                                itemIndex === index ? { ...item, title: event.target.value } : item
                              )))}
                              className="mt-2 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                            />
                            {button.kind === "url" ? (
                              <input
                                value={button.url || ""}
                                placeholder="https://"
                                onChange={(event) => setButtons((current) => current.map((item, itemIndex) => (
                                  itemIndex === index ? { ...item, url: event.target.value } : item
                                )))}
                                className="mt-2 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                              />
                            ) : null}
                          </div>
                        ))}
                        {buttons.length < 3 ? (
                          <button
                            type="button"
                            onClick={() => setButtons((current) => current.concat({ title: "", triggerId: "", kind: "quick", url: "" }))}
                            className="mt-2 text-sm font-medium text-blue-600"
                          >
                            Add a button
                          </button>
                        ) : null}
                      </div>
                  </div>
                <label className="mt-3 flex items-center gap-2 text-sm text-gray-800">
                  <input type="checkbox" checked={sendPhone} onChange={(event) => setSendPhone(event.target.checked)} />
                  Phone number
                </label>
              </div>
            ) : null}
            {tab === "whatsapp" ? (
              <div className="mt-3">
                <label className="block text-sm text-gray-600">
                  Approved template
                  <select
                    value={templates.find((item) => item.name === templateName)?.id || ""}
                    onChange={(event) => chooseTemplate(event.target.value)}
                    className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  >
                    <option value="">{templatesLoading ? "Loading templates..." : "Choose a template"}</option>
                    {templates.map((item) => (
                      <option key={item.id} value={item.id}>{item.name}</option>
                    ))}
                  </select>
                </label>
                {templateName ? (
                  <p className="mt-2 text-xs text-gray-500">
                    {templateName}{languageCode ? ` · ${languageCode}` : ""}
                  </p>
                ) : null}
                {inputs.map((input, index) => (
                  <label key={input.name || index} className="mt-3 block text-sm text-gray-600">
                    What the model puts in {`{{${index + 1}}}`}
                    <input
                      value={input.description}
                      onChange={(event) => setInputs((current) => current.map((item, itemIndex) => (
                        itemIndex === index ? { ...item, description: event.target.value } : item
                      )))}
                      className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                    />
                  </label>
                ))}
                {needsImage ? (
                  <div className="mt-3">
                    <p className="text-sm text-gray-600">Image for the template header</p>
                    <div className="mt-1 flex gap-2">
                      <label
                        className={`flex cursor-pointer items-center rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-blue-600 ${
                          uploading ? "pointer-events-none opacity-50" : ""
                        }`}
                      >
                        {uploading ? "Uploading..." : "Upload"}
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(event) => {
                            const file = event.target.files?.[0];
                            event.target.value = "";
                            uploadImage(file);
                          }}
                        />
                      </label>
                      <button
                        type="button"
                        onClick={() => setLibraryOpen((openNow) => !openNow)}
                        className="rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700"
                      >
                        Library
                      </button>
                    </div>
                    {libraryOpen ? (
                      <ImageLibrary
                        selectedUrl={imageUrl}
                        onClose={() => setLibraryOpen(false)}
                        onSelect={(url) => {
                          setImageUrl(url);
                          setLibraryOpen(false);
                        }}
                      />
                    ) : null}
                    {imageUrl ? (
                      <div className="mt-2 flex items-center gap-3">
                        <img src={imageUrl} alt="" className="h-16 w-16 rounded object-cover" />
                        <button type="button" onClick={() => setImageUrl("")} className="text-sm text-gray-500">
                          Remove image
                        </button>
                      </div>
                    ) : null}
                  </div>
                ) : null}
                {templateReady ? (
                  <div className="mt-3 w-44 overflow-hidden rounded-lg bg-[#dcf8c6]">
                    {imageUrl ? <img src={imageUrl} alt="" className="h-20 w-full object-cover" /> : null}
                    <p className="whitespace-pre-wrap px-2 py-1.5 text-[11px] leading-snug text-gray-800">
                      {previewText(templateBody, inputs) || templateName}
                    </p>
                    {templateButtons.map((label) => (
                      <p key={label} className="border-t border-black/10 px-2 py-1 text-center text-[11px] text-blue-700">{label}</p>
                    ))}
                  </div>
                ) : null}
              </div>
            ) : null}
            {tab === "web" ? (
              <fieldset className="mt-3">
                <legend className="text-sm text-gray-600">The message can include</legend>
                <label className="mt-2 flex items-center gap-2 text-sm text-gray-800">
                  <input type="checkbox" checked={sendPhone} onChange={(event) => setSendPhone(event.target.checked)} />
                  Phone number
                </label>
                <label className="mt-2 flex items-center gap-2 text-sm text-gray-800">
                  <input type="checkbox" checked={sendText} onChange={(event) => setSendText(event.target.checked)} />
                  Text
                </label>
              </fieldset>
            ) : null}
            {error ? <p className="mt-4 text-sm text-red-600">{error}</p> : null}
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={closeForm} className="rounded-lg px-4 py-2 text-sm text-gray-600">
                Cancel
              </button>
              <button
                type="button"
                onClick={sendRequest}
                disabled={
                  sending
                  || uploading
                  || !name.trim()
                  || !detail.trim()
                  || (tab === "instagram" && !sendPhone && !messageText.trim() && !imageUrl)
                  || (tab === "web" && !sendPhone && !sendText)
                  || (tab === "whatsapp" && (!templateName.trim() || !languageCode.trim() || (needsImage && !imageUrl)))
                }
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white disabled:bg-gray-200"
              >
                {editingId || tab !== "platform" ? "Save" : "Send request"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
